import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { API_ENDPOINTS, API_URL } from '../config/constants.ts';

// Helper to detect iOS/Safari and PWA mode
export const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
export const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
export const isInStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as any).standalone === true;

export const isPushNotificationSupported =
  !isIOS || (isIOS && isInStandaloneMode());

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyDVNF-gsXJmRMb-q-RU_qGfITc-vh3Ik50",
  authDomain: "ultra-5827b.firebaseapp.com",
  projectId: "ultra-5827b",
  storageBucket: "ultra-5827b.appspot.com",
  messagingSenderId: "157349571397",
  appId: "1:157349571397:web:ed007e3ab0dc5309132c2c",
  measurementId: "G-44RQ7BSE75"
};

// Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig);
const messaging = getMessaging(firebaseApp);

// Track the last token that was sent to the server to avoid duplicate requests
let lastSentToken = '';

/**
 * Request notification permission and get FCM token
 */
export const requestNotificationPermission = async (): Promise<string | null> => {
  if (!isPushNotificationSupported) {
    console.log('Push notifications are not supported on iOS/Safari browsers.');
    return null;
  }
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return null;
  }

  // On Android, the web Notification API should trigger native permission request
  // For Android 13+ (API 33+), this will automatically request POST_NOTIFICATIONS permission
  let permission = Notification.permission;
  
  if (permission !== 'granted') {
    try {
      // Request permission - on Android this will show the native permission dialog
      permission = await Notification.requestPermission();
      
      // Log for debugging
      if (Capacitor.isNativePlatform()) {
        console.log(`[Android] Notification permission result: ${permission}`);
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return null;
    }
  }
  
  if (permission !== 'granted') {
    console.warn('Notification permission not granted:', permission);
    return null;
  }
  
  try {
    // Register service worker
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    await navigator.serviceWorker.ready;
    
    // Get token from Firebase
    const currentToken = await getToken(messaging, {
      serviceWorkerRegistration: registration
    });
    
    if (!currentToken) {
      return null;
    }
    
    localStorage.setItem('fcm_token', currentToken);
    
    // Only update the token on the server if it's different from the last one we sent
    if (currentToken !== lastSentToken) {
      try {
        const authToken = localStorage.getItem('auth_token');
        if (authToken) {
          console.log('Updating FCM token on server...');
          const response = await fetch(`${API_URL}${API_ENDPOINTS.user.updateUserToken}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({
              token: currentToken
            })
          });
          
          if (response.ok) {
            console.log('Token successfully saved to backend');
            // Update the last sent token
            lastSentToken = currentToken;
          } else {
            console.error('Failed to save token to backend:', response.status);
          }
        }
      } catch (error) {
        console.error('Error saving token to backend:', error);
      }
    } else {
      console.log('Token already sent to server, skipping update');
    }
    
    return currentToken;
  } catch (error) {
    console.error('Error getting notification token:', error);
    return null;
  }
};

/**
 * Handle message received in the foreground
 */
export const onMessageListener = () => {
  return new Promise((resolve) => {
    onMessage(messaging, (payload) => {
      resolve(payload);
    });
  });
};

/**
 * Initialize Firebase messaging when the app starts
 * Should be called from App component or main entry point
 */
export const initializeFirebaseMessaging = async (): Promise<void> => {
  try {
    console.log('Initializing Firebase Messaging...');
    const token = await requestNotificationPermission();
    if (token) {
      console.log('Firebase Messaging initialized successfully with token:', token);
    } else {
      console.log('Firebase Messaging initialization did not get a token');
    }
  } catch (error) {
    console.error('Error initializing Firebase Messaging:', error);
  }
};

export default {
  requestNotificationPermission,
  onMessageListener,
  initializeFirebaseMessaging,
  isPushNotificationSupported
}; 